import { useRef } from 'react';
import {
  Box,
  FlexGrid,
  FlexGridColumn,
  Icon,
  IconButton,
  Text,
  Textarea,
  uniqueId,
} from '@hs-baumappe/legacy-ui';
import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { FormikFormField } from '@hs-baumappe/forms';
import { OvertimeFormValues } from '../../form';
import { formatDateToDateString } from '../../../../utils/date';
import FormikProfessionsField from '../../../FormikProfessionsField';

interface ProjectInformationSectionProps {
  nextSuggestedNumber?: number;
  professions: string[];
  disableProjectNumberField?: boolean;
  pdfRedesignEnabled: boolean;
}

export default function ProjectInformationSection({
  nextSuggestedNumber,
  professions,
  disableProjectNumberField,
  pdfRedesignEnabled,
}: ProjectInformationSectionProps): JSX.Element {
  const { t } = useTranslation();
  const informationTitleId = useRef(uniqueId('information-title-'));

  const [noField, , noFieldHelpers] = useField<OvertimeFormValues['no']>('no');
  const [projectNumberField] = useField<OvertimeFormValues['projectNumber']>('projectNumber');
  const [orderDateField] = useField<OvertimeFormValues['orderDate']>('orderDate');

  return (
    <Box role="group" aria-labelledby={informationTitleId.current}>
      <Text id={informationTitleId.current} variant="title-small" color="muted">
        {t('overtimeForm.information.title')}
      </Text>

      <FlexGrid>
        <FlexGridColumn sm={6} stylex={{ marginTop: 'small-x' }}>
          <FormikFormField
            name="no"
            label={t('overtimeForm.information.reportNo.label')}
            placeholder={
              typeof nextSuggestedNumber === 'number' ? nextSuggestedNumber.toString() : undefined
            }
            endSection={
              noField.value ? (
                <IconButton
                  type="button"
                  icon={<Icon name="undo" />}
                  onClick={() => noFieldHelpers.setValue('')}
                  aria-label={t('overtimeForm.information.reportNo.undoButton.label')}
                />
              ) : undefined
            }
          />
        </FlexGridColumn>
        <FlexGridColumn sm={6} stylex={{ marginTop: 'small-x' }}>
          <FormikFormField
            type="date"
            name="date"
            label={t('overtimeForm.information.date.label')}
            placeholder={t('overtimeForm.information.date.placeholder')}
            max={formatDateToDateString(new Date())}
          />
        </FlexGridColumn>
      </FlexGrid>

      <FlexGrid>
        <FlexGridColumn sm={6} stylex={{ marginTop: 'small-x' }}>
          <FormikFormField
            name="projectNumber"
            label={t(
              pdfRedesignEnabled
                ? 'overtimeForm.information.projectNumber.redesign.label'
                : 'overtimeForm.information.projectNumber.label',
            )}
            placeholder={t(
              pdfRedesignEnabled
                ? 'overtimeForm.information.projectNumber.redesign.placeholder'
                : 'overtimeForm.information.projectNumber.placeholder',
            )}
            contentEditable={!disableProjectNumberField}
            disabled={disableProjectNumberField}
            value={projectNumberField.value || '-'}
          />
        </FlexGridColumn>
        <FlexGridColumn sm={6} stylex={{ marginTop: 'small-x' }}>
          <FormikFormField
            name="clientName"
            label={t('overtimeForm.information.clientName.label')}
            placeholder={t('overtimeForm.information.clientName.placeholder')}
          />
        </FlexGridColumn>
      </FlexGrid>

      {pdfRedesignEnabled && (
        <>
          <FlexGrid>
            <FlexGridColumn sm={6} stylex={{ marginTop: 'small-x' }}>
              <FormikProfessionsField name="professions" professions={professions} />
            </FlexGridColumn>
            <FlexGridColumn sm={6} stylex={{ marginTop: 'small-x' }}>
              <FormikFormField
                name="clientReference"
                label={t('overtimeForm.information.clientReference.label')}
                placeholder={t('overtimeForm.information.clientReference.placeholder')}
              />
            </FlexGridColumn>
          </FlexGrid>
          <Box stylex={{ marginTop: 'small-x' }}>
            <FormikFormField<typeof Textarea>
              control={Textarea}
              name="description"
              label={t('overtimeForm.information.description.label')}
              placeholder={t('overtimeForm.information.description.placeholder')}
              minRows={3}
              maxRows={6}
            />
          </Box>
        </>
      )}

      <FlexGrid>
        <FlexGridColumn sm={6} stylex={{ marginTop: 'small-x' }}>
          <FormikFormField
            type="date"
            name="orderDate"
            label={t('overtimeForm.information.orderDate.label')}
            placeholder={t('overtimeForm.information.orderDate.placeholder')}
            max={formatDateToDateString(new Date())}
          />
        </FlexGridColumn>
        <FlexGridColumn sm={6} stylex={{ marginTop: 'small-x' }}>
          <FormikFormField
            type="date"
            name="endDate"
            label={t('overtimeForm.information.endDate.label')}
            placeholder={t('overtimeForm.information.endDate.placeholder')}
            min={orderDateField.value}
          />
        </FlexGridColumn>
      </FlexGrid>
    </Box>
  );
}
